import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Radio, Space, Typography, message } from 'antd';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { ConfirmResponse, PublicProposal } from '../../../private/modules/job-application/models/interview.model';
import { InterviewService } from '../../../private/modules/job-application/services/interview.service';

const InterviewConfirmPage: React.FC = () => {
  const { token = '' } = useParams();
  const [proposal, setProposal] = useState<PublicProposal | null>(null);
  const [gone, setGone] = useState(false);
  const [slotId, setSlotId] = useState<string>();
  const [result, setResult] = useState<ConfirmResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await InterviewService.getPublicProposal(token);
        if (!cancelled) {
          setProposal(data);
          if (data.slots?.[0]) {
            setSlotId(data.slots[0].id);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setGone(error instanceof Error && error.message === 'GONE');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const confirm = async () => {
    if (!slotId) {
      message.error('Select a time slot');
      return;
    }
    setSubmitting(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await InterviewService.confirmPublic(token, slotId, timezone);
      setResult(response);
    } catch (error) {
      if (error instanceof Error && error.message === 'GONE') {
        setGone(true);
      } else {
        message.error('Could not confirm this interview');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <Typography.Text>Loading…</Typography.Text>
      </div>
    );
  }

  if (gone) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <Alert
          type="warning"
          showIcon
          message="This interview link is no longer available"
          description="It may have expired, been confirmed already, or been cancelled."
        />
      </div>
    );
  }

  if (result) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-lg">
          <Typography.Title level={3}>Interview confirmed</Typography.Title>
          <Typography.Paragraph>
            {result.title}
            <br />
            {dayjs(result.startsAt).format('YYYY-MM-DD HH:mm')} ({result.timezone || 'UTC'})
          </Typography.Paragraph>
          <Button type="primary" href={InterviewService.publicIcsUrl(token)}>
            Download calendar invite (.ics)
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-lg">
        <Typography.Title level={3}>{proposal?.title}</Typography.Title>
        <Typography.Paragraph type="secondary">
          {proposal?.meetingType} · {proposal?.durationMinutes} minutes
          {proposal?.locationOrLink ? ` · ${proposal.locationOrLink}` : ''}
        </Typography.Paragraph>
        <Typography.Paragraph>Choose one time slot:</Typography.Paragraph>
        <Radio.Group
          value={slotId}
          onChange={(e) => setSlotId(e.target.value)}
          className="mb-4 w-full"
        >
          <Space direction="vertical" className="w-full">
            {proposal?.slots?.map((slot) => (
              <Radio key={slot.id} value={slot.id}>
                {dayjs(slot.startsAt).format('ddd, MMM D · HH:mm')} UTC
              </Radio>
            ))}
          </Space>
        </Radio.Group>
        <Button type="primary" block loading={submitting} onClick={confirm}>
          Confirm interview
        </Button>
      </Card>
    </div>
  );
};

export default InterviewConfirmPage;
